"use client";

import type { ColumnDef } from "@tanstack/react-table";

import { Badge } from "@/components/ui/badge";
import { DataTableColumnHeader } from "@/lib/data-table-column-header";
import type { Invoice } from "@/types";
import { formatDateAR } from "@/utils/date";
import { truncateMiddle } from "@/utils/text";

const statusColors: Record<string, string> = {
  uploaded: "bg-blue-500",
  processing: "bg-yellow-500",
  pending_review: "bg-orange-500",
  reviewed: "bg-purple-500",
  assigned: "bg-green-500",
  error: "bg-red-500",
};

const statusLabels: Record<string, string> = {
  uploaded: "Cargada",
  processing: "Procesando",
  pending_review: "Pendiente de revisión",
  reviewed: "Revisada",
  assigned: "Asignada",
  error: "Error",
};

export const columns: ColumnDef<Invoice>[] = [
  {
    accessorKey: "id",
    header: ({ column }) => (
      <DataTableColumnHeader column={column} title="ID" />
    ),
    cell: ({ row }) => {
      const invoice = row.original;
      return <div className="font-mono font-medium">{invoice.id}</div>;
    },
  },
  {
    accessorKey: "invoice_number",
    header: ({ column }) => (
      <DataTableColumnHeader column={column} title="Número de Factura" />
    ),
    cell: ({ row }) => {
      const invoiceNumber = row.original.invoice_number;
      if (!invoiceNumber) {
        return <div className="text-muted-foreground">-</div>;
      }
      return <div className="font-medium">{invoiceNumber}</div>;
    },
  },
  {
    accessorKey: "supplier.name",
    header: ({ column }) => (
      <DataTableColumnHeader column={column} title="Proveedor" />
    ),
    cell: ({ row }) => {
      const supplier = row.original.supplier;
      const supplierName = supplier?.name;

      if (!supplierName) {
        return <div className="text-muted-foreground">-</div>;
      }

      const truncatedName = truncateMiddle(supplierName, 20);
      const shouldShowTooltip = supplierName.length > 20;

      if (shouldShowTooltip) {
        return (
          <div className="cursor-help" title={supplierName}>
            {truncatedName}
          </div>
        );
      }

      return <div>{supplierName}</div>;
    },
  },
  {
    accessorKey: "purchase_request.sector.name",
    header: ({ column }) => (
      <DataTableColumnHeader column={column} title="Obra" />
    ),
    cell: ({ row }) => {
      const sectorName = row.original.purchase_request?.sector?.name;

      if (!sectorName) {
        return <div className="text-muted-foreground">-</div>;
      }

      const truncatedName = truncateMiddle(sectorName, 20);
      const shouldShowTooltip = sectorName.length > 20;

      if (shouldShowTooltip) {
        return (
          <div className="cursor-help" title={sectorName}>
            {truncatedName}
          </div>
        );
      }

      return <div>{sectorName}</div>;
    },
  },
  {
    accessorKey: "status",
    header: ({ column }) => (
      <DataTableColumnHeader column={column} title="Estado" />
    ),
    cell: ({ row }) => {
      const status = row.getValue("status");
      const color = statusColors[status as keyof typeof statusColors] ?? "";
      const label = statusLabels[status as keyof typeof statusLabels] ?? status;
      return <Badge className={color}>{label}</Badge>;
    },
  },
  {
    accessorKey: "total_amount",
    header: ({ column }) => (
      <DataTableColumnHeader column={column} title="Monto Total" />
    ),
    cell: ({ row }) => {
      const totalAmount = row.getValue("total_amount");
      if (!totalAmount) {
        return <div className="text-muted-foreground">-</div>;
      }
      return <div>${(totalAmount as number).toLocaleString()}</div>;
    },
  },
  {
    accessorKey: "payment_method",
    header: ({ column }) => (
      <DataTableColumnHeader column={column} title="Condición de Pago" />
    ),
    cell: ({ row }) => {
      const paymentMethod = row.getValue("payment_method");
      if (!paymentMethod) {
        return <div className="text-muted-foreground">-</div>;
      }
      return <div>{paymentMethod}</div>;
    },
  },
  {
    accessorKey: "created_at",
    header: ({ column }) => (
      <DataTableColumnHeader column={column} title="Fecha de Creación" />
    ),
    cell: ({ row }) => {
      const date = row.getValue("created_at");
      return <div>{formatDateAR(date as string | number | Date)}</div>;
    },
  },
];
